import {useEffect, useRef, useState} from "react";

const KLASSE = "dhpt-markiert";
const KLASSE_ZEILE = "dhpt-markiert-zeile";
// Namen werden an manchen Stellen gekürzt, z.B. "Vorname Nachname Langer..." (siehe PublicTournamentHighlightsTab)
const KUERZUNG_LAENGE = 25;

function normalisiere(text) {
    return text.replace(/\s+/g, " ").trim().toLowerCase();
}

/**
 * Prüft, ob ein angezeigter Text den Spieler meint. Erkannt werden auch Doppel ("A / B"),
 * Platzierungen ("1. Name"), Zusätze in Klammern und gekürzte Namen.
 */
function passtZumSpieler(text, name) {
    const teile = text.split(/\s*(?:\/|&|,|\|)\s*|\s+vs\.?\s+/i);
    for (let teil of teile) {
        teil = teil.replace(/^\d+\.\s*/, "").replace(/\s*\([^)]*\)$/, "").trim();
        if (teil === "") {
            continue;
        }
        if (teil === name) {
            return true;
        }
        if (name.length > KUERZUNG_LAENGE && teil === name.substring(0, KUERZUNG_LAENGE) + "...") {
            return true;
        }
    }
    return false;
}

function entferneMarkierungen(container) {
    container.querySelectorAll("." + KLASSE + ", ." + KLASSE_ZEILE).forEach(element => {
        element.classList.remove(KLASSE);
        element.classList.remove(KLASSE_ZEILE);
    });
}

/**
 * Hebt einen Spieler überall im Container hervor, ohne dass die angezeigten Komponenten (Gruppen, Baum, Tabellen)
 * davon wissen müssen. Die Markierung erfolgt über CSS-Klassen am DOM und wird bei jeder Änderung des Inhalts
 * (z.B. Live-Update per Websocket, Wechsel des Reiters) neu gesetzt.
 *
 * @param ansichtKey ändert sich beim Wechsel des Bereichs, damit dort wieder zum ersten Treffer gesprungen wird
 * @returns die Anzahl der Treffer im aktuell angezeigten Inhalt
 */
export default function useSpielerHervorhebung(containerRef, spielername, ansichtKey) {

    const [anzahlTreffer, setAnzahlTreffer] = useState(0);
    const letzterNameRef = useRef(undefined);

    useEffect(() => {
        letzterNameRef.current = undefined;
    }, [ansichtKey]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }
        const name = spielername ? normalisiere(spielername) : "";

        let frame = undefined;
        const markiere = () => {
            frame = undefined;
            entferneMarkierungen(container);
            if (name === "") {
                setAnzahlTreffer(0);
                return;
            }
            const treffer = [];
            const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
            let knoten = walker.nextNode();
            while (knoten) {
                const element = knoten.parentElement;
                if (element && !element.closest("input, textarea, script, style") && passtZumSpieler(normalisiere(knoten.nodeValue), name)) {
                    // in Tabellen wird die ganze Zeile markiert, sonst das Element mit dem Namen
                    const zeile = element.closest("tr");
                    if (zeile && container.contains(zeile)) {
                        zeile.classList.add(KLASSE_ZEILE);
                        treffer.push(zeile);
                    } else {
                        element.classList.add(KLASSE);
                        treffer.push(element);
                    }
                }
                knoten = walker.nextNode();
            }
            setAnzahlTreffer(new Set(treffer).size);

            // nur bei einer neuen Auswahl zum ersten Treffer springen, nicht bei jedem Live-Update
            if (letzterNameRef.current !== name && treffer.length > 0) {
                letzterNameRef.current = name;
                treffer[0].scrollIntoView({block: "center", inline: "center", behavior: "smooth"});
            }
        };
        const planeMarkierung = () => {
            if (frame === undefined) {
                frame = window.requestAnimationFrame(markiere);
            }
        };

        if (name === "") {
            letzterNameRef.current = undefined;
        }
        markiere();
        // Klassenänderungen (attributes) werden bewusst nicht beobachtet, sonst würde sich die Markierung selbst auslösen
        const observer = new MutationObserver(planeMarkierung);
        observer.observe(container, {childList: true, subtree: true, characterData: true});

        return () => {
            observer.disconnect();
            if (frame !== undefined) {
                window.cancelAnimationFrame(frame);
            }
            entferneMarkierungen(container);
        };
    }, [containerRef, spielername, ansichtKey]);

    return anzahlTreffer;
}
